import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Theme } from '../../assets/themes';
import storage from '../storage';
import { RootState } from '@state/types';
import { settingsActions } from '@state/settings';
import {
  DARK_SCHEME_QUERY,
  DarkHours,
  DEFAULT_DARK_HOURS,
  isDarkHour,
  minuteOfDay,
  msUntilNextChange,
  parseDarkHours,
  parseThemePreference,
  resolveTheme,
  systemPrefersDark,
  ThemePreference
} from './themePreference';

type ThemeMode = {
  /** What the student picked: light, dark, Auto or set hours. */
  preference: ThemePreference;
  /** What shows now, with Auto worked out from the device and set hours from the time of day. */
  theme: Theme;
  /** When set hours go dark and back to light. */
  darkHours: DarkHours;
  setPreference: (preference: ThemePreference) => void;
  setDarkHours: (hours: DarkHours) => void;
};

const ThemeModeContext = createContext<ThemeMode>({
  preference: 'light',
  theme: 'light',
  darkHours: DEFAULT_DARK_HOURS,
  setPreference: () => {},
  setDarkHours: () => {}
});

// The device's own light or dark, followed as it changes while Auto is on (a laptop going dark at sunset).
const useSystemDark = (listening: boolean) => {
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (!listening || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const onChange = () => setSystemDark(query.matches);
    onChange();
    // Safari before 14 only has the older listener methods.
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else query.removeListener(onChange);
    };
  }, [listening]);

  return systemDark;
};

// Whether set hours are dark now, checked again as they next change, and when the student comes back to the tab: a
// sleeping laptop holds its timers, so one set for 8pm can come due long after.
const useInDarkHours = (hours: DarkHours, listening: boolean) => {
  const [inDarkHours, setInDarkHours] = useState(() => isDarkHour(minuteOfDay(), hours));

  useEffect(() => {
    const check = () => setInDarkHours(isDarkHour(minuteOfDay(), hours));
    check();
    if (!listening) return;
    let timer: number;
    const schedule = () => {
      window.clearTimeout(timer);
      // A second past the minute, so the check lands after it has turned.
      timer = window.setTimeout(() => {
        check();
        schedule();
      }, msUntilNextChange(new Date(), hours) + 1000);
    };
    const onReturn = () => {
      check();
      schedule();
    };
    schedule();
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [hours, listening]);

  return inDarkHours;
};

/**
 * One light-or-dark for the whole app, shared by the sidebar button and Settings (Petr, 2026-10-08: Auto, following the
 * device, and set hours). It used to be App's own state, which nothing else could change.
 *
 * The choice is the account's, as the rest of the settings are (Petr, 2026-10-08). This browser keeps a copy, which the
 * app starts in and which signed-out pages show; once the account's settings are in, they win, and a change is saved
 * to both.
 */
export const ThemeModeProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const accountTheme = useSelector((state: RootState) => state.settings.userSettings?.theme);
  const accountHours = useSelector((state: RootState) => state.settings.userSettings?.darkHours);
  const signedIn = useSelector((state: RootState) => !!state.settings.userSettings);
  const [preference, setPreferenceState] = useState<ThemePreference>(storage.getThemePreference);
  const [darkHours, setDarkHoursState] = useState<DarkHours>(storage.getThemeDarkHours);
  const systemDark = useSystemDark(preference === 'auto');
  const inDarkHours = useInDarkHours(darkHours, preference === 'scheduled');
  const theme = resolveTheme(preference, systemDark, inDarkHours);

  // The account's, as it arrives or changes, copied to this browser for the next start.
  useEffect(() => {
    if (!accountTheme) return;
    const next = parseThemePreference(accountTheme);
    storage.setThemePreference(next);
    setPreferenceState(next);
  }, [accountTheme]);

  useEffect(() => {
    if (!accountHours) return;
    const next = parseDarkHours(JSON.stringify(accountHours));
    storage.setThemeDarkHours(next);
    setDarkHoursState(current => (current.from === next.from && current.to === next.to ? current : next));
  }, [accountHours]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      storage.setThemePreference(next);
      setPreferenceState(next);
      if (signedIn) dispatch(settingsActions.saveUserSettings.request({ theme: next }));
    },
    [signedIn, dispatch]
  );

  const setDarkHours = useCallback(
    (hours: DarkHours) => {
      storage.setThemeDarkHours(hours);
      setDarkHoursState(hours);
      if (signedIn) dispatch(settingsActions.saveUserSettings.request({ darkHours: hours }));
    },
    [signedIn, dispatch]
  );

  const value = useMemo(() => ({ preference, theme, darkHours, setPreference, setDarkHours }), [
    preference,
    theme,
    darkHours,
    setPreference,
    setDarkHours
  ]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => useContext(ThemeModeContext);
